import ArrowDownIcon from '@ben/icons/arrow-down-s-line';
import {
  Select as MantineSelect,
  type ComboboxData,
  type ComboboxGenericItem,
  type ComboboxItem,
  type SelectProps as MantineSelectProps,
} from '@mantine/core';
import clsx from 'clsx';
import { isString } from 'lodash';
import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import InputFeedback from '../input-feedback';
import InputLabelWithHint from '../input-label/input-label-with-hint';
import Loader from '../loader';
import Icon from '../remix-icon';
import styles from './select.module.css';

const flattenComboboxData = (
  data: ComboboxData
): (string | ComboboxItem | ComboboxGenericItem)[] =>
  data.flatMap((item) =>
    !isString(item) && 'group' in item ? item.items : [item]
  );

const findLabelInData = (
  data: ComboboxData | undefined,
  value: string | null | undefined
): string | undefined => {
  if (!data || !value) return undefined;

  const match = flattenComboboxData(data).find((item) =>
    isString(item) ? item === value : item.value === value
  );

  if (!match) return undefined;
  if (isString(match)) return match;
  return 'label' in match ? match.label : String(match.value);
};

export interface SelectProps extends Omit<
  MantineSelectProps,
  'onChange' | 'label'
> {
  onChange?: (value?: string) => void;
  infoMessage?: string;
  errorMessage?: string;
  warningMessage?: string;
  successMessage?: string;
  label?: string | ReactNode; // Mantine label is of type ReactNode
  clearOnEmptySearch?: boolean;
  loading?: boolean;
}

export const Select = ({
  onChange,
  onSearchChange,
  infoMessage,
  errorMessage,
  warningMessage,
  successMessage,
  label,
  description,
  clearOnEmptySearch,
  loading,
  size = 'sm',
  ...rest
}: SelectProps) => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState('');
  const defaultId = useId();
  const selectId = rest.id ?? defaultId;

  const onInternalSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      onSearchChange?.(value);
      if (clearOnEmptySearch && value === '') {
        onChange?.();
      }
    },
    [clearOnEmptySearch, onChange, onSearchChange]
  );

  useEffect(() => {
    if (!rest.value) {
      // Reset search value when select value is cleared
      setSearchValue('');
    }
  }, [rest.value]);

  const rightSection = useMemo(() => {
    if (loading) {
      return <Loader loaderProps={{ size: size === 'xs' ? 'xs' : 'sm' }} />;
    }
    return (
      <Icon
        icon={ArrowDownIcon}
        color={theme.palette.primary.main}
        className={clsx('mantine-Select-chevron', styles.chevron)}
        size={size === 'xs' ? 14 : 18}
      />
    );
  }, [loading, size, theme.palette.primary.main]);

  return (
    <div style={{ width: rest.width }}>
      <MantineSelect
        checkIconPosition="right"
        withScrollArea={true}
        allowDeselect={false}
        rightSection={rightSection}
        onChange={(value) => onChange?.(value ?? undefined)}
        size={size}
        {...rest}
        id={selectId}
        label={
          // To satisfy the usage of this component in the proxy
          isString(label) ? (
            <InputLabelWithHint
              name={label}
              hintText={description}
              showLabel
              inputId={selectId}
            />
          ) : (
            label
          )
        }
        title={rest.title ?? findLabelInData(rest.data, rest.value)}
        comboboxProps={{
          position: 'bottom-start',
          ...rest.comboboxProps,
          offset: 4,
          zIndex: 1001,
        }}
        searchValue={searchValue}
        onSearchChange={onInternalSearchChange}
        scrollAreaProps={{
          type: 'hover',
          scrollbarSize: '0.5rem',
          ...rest.scrollAreaProps,
        }}
        classNames={{
          root: styles.root,
          wrapper: styles.wrapper,
          input: styles.input,
          dropdown: styles.dropdown,
          options: styles.options,
          option: styles.option,
          label: styles.label,
          empty: styles.empty,
        }}
      />
      <InputFeedback
        infoMessage={infoMessage}
        errorMessage={errorMessage}
        warningMessage={warningMessage}
        successMessage={successMessage}
      />
    </div>
  );
};

export default memo(Select);
